import type StateService from 'garaje/services/state';

/**
 * Billing-plan gating for the HRIS Lifecycle routine (EIP-3653).
 *
 * A routine step renders only when the customer's plan for the gate's product
 * includes at least one of the gate's feature codes. The product scoping is
 * load-bearing: `access-control-integrations` also ships on Visitors Enterprise
 * plans, but the ACS deprovision step unlocks on Employee Experience (empxp)
 * tiers only — so each gate names which subscription's features to consult.
 *
 * Entitlement reads the COMPANY-scoped subscription slots, never the
 * route-scoped getters: the routine is one company-wide config (its snapshot
 * endpoint is keyed by company_id only), so nav, the company route, and the
 * per-location route must all gate identically even under mixed-plan billing.
 *
 * Future steps drop into HRIS_STEP_BILLING keyed by their StepDef id; the
 * HrisStepId union keeps the map and the controller's STEPS in lockstep.
 */
export type HrisBillingProduct = 'visitors' | 'workplace';

export type HrisStepId = 'acs' | 'watchList' | 'blocklist' | 'forceSignout';

export interface HrisBillingGate {
  product: HrisBillingProduct;
  /** Entitled when the product subscription includes ANY of these codes. */
  anyOf: readonly string[];
}

export const HRIS_STEP_BILLING: Record<HrisStepId, HrisBillingGate> = {
  acs: { product: 'workplace', anyOf: ['access-control-integrations'] },
  watchList: { product: 'visitors', anyOf: ['blacklist'] },
  blocklist: { product: 'visitors', anyOf: ['blacklist'] },
  forceSignout: { product: 'workplace', anyOf: ['mobile_gps_auto_sign_in', 'access_control_auto_sign_in'] },
};

/**
 * Steps rendered as per-location sub-rows (StepDef.kind 'perSystem'). These are
 * the only steps a location-scoped reader can see — org (company-scope) steps
 * require company read, mirroring the backend's filterByReadScope — so a
 * location-only caller's visit gate needs one of THESE entitled, not just any.
 */
export const HRIS_PER_LOCATION_STEP_IDS: ReadonlySet<HrisStepId> = new Set(['acs']);

/**
 * The routine's trigger is installing the HRIS integration itself. Its gate is
 * deliberately NOT part of step/menu visibility: a customer entitled to any step
 * body still sees the routine, and the "you need the HRIS integration" story is
 * surfaced elsewhere. No consumer yet — reserved for EIP-3608, which reads the
 * same mapping.
 */
export const HRIS_TRIGGER_BILLING: HrisBillingGate = {
  product: 'visitors',
  anyOf: ['hr-recruiting-integrations'],
};

export function isEntitledTo(state: StateService, gate: HrisBillingGate): boolean {
  const productSubscription =
    gate.product === 'visitors' ? state.companyVisitorsSubscription : state.companyWorkplaceSubscription;
  // Platform plans bundle every product into one subscription (billing puts
  // e.g. empxp codes on platform/enterprise), so the platform slot always
  // counts toward the gate alongside the product's own subscription.
  //
  // Known limit: under the locationBilling flag the company slots mirror the
  // selected location's subscriptions — there is no company-scoped source in
  // that mode (see subscription-state.ts), so entitlement follows the active
  // location like every other billing-gated surface. The company-canonical
  // answer for such companies belongs to server-side enforcement.
  const features = [...(productSubscription?.features ?? []), ...(state.companyPlatformSubscription?.features ?? [])];
  return gate.anyOf.some((code) => features.includes(code));
}

/** Step ids (StepDef.id) the current company's billing plans entitle it to. */
export function entitledHrisStepIds(state: StateService): Set<HrisStepId> {
  const ids = Object.keys(HRIS_STEP_BILLING) as HrisStepId[];
  return new Set(ids.filter((id) => isEntitledTo(state, HRIS_STEP_BILLING[id])));
}
